import React, { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { request, useOperations } from './OperationsProvider';
import useOperationalPermissions from '../common/util/operationalPermissions';
import { deviceAvailable } from './devices';

const documentKinds = ['Registration', 'Insurance', 'PUC', 'Permit'];

export const blankVehicle = () => ({
  name: '', vehicleType: 'Truck', owner: '', transporter: '', driverName: '', driverMobile: '', rfid: '', gps: '', assignmentMode: 'TEMPORARY', documents: documentKinds.map((kind) => ({ kind, reference: '', expiresOn: '' })),
});

// Registering a truck belongs wherever a truck is admitted — the parking counter or the gate — not on
// a separate screen the clerk has to leave for. This posts to the same endpoint as the full wizard,
// and that endpoint also creates the driver record, so the driver list fills itself.
//
// Devices already registered can be attached here. Enrolling a brand-new one stays with a tenant
// administrator, and a permanently fitted GPS unit does too, which is why those are offered as
// pickers of known devices rather than free text.
const AddVehicleDialog = ({ open, draft, onChange, onClose, onCreated }) => {
  const { data, refresh } = useOperations();
  const { administer } = useOperationalPermissions();
  const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const documents = draft?.documents || [];
  const incomplete = documents.some((d) => !!d.reference !== !!d.expiresOn);
  const available = (kind) => data.sources.filter((s) => s.kind === kind && deviceAvailable(s, data.assignments));
  const save = async (event) => {
    event.preventDefault();
    if (incomplete) { setError('Give both the reference and the expiry date for each document you record.'); return; }
    setSaving(true); setError('');
    try {
      const created = await request('/api/operations/vehicle-registration', 'POST', {
        ...draft, rfidAssignmentMode: 'PERMANENT', documents: documents.filter((d) => d.reference && d.expiresOn),
      });
      await refresh();
      onCreated(created);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };
  const field = (key, label) => (
    <TextField fullWidth required label={label} value={draft?.[key] || ''} disabled={saving} inputProps={{ maxLength: 100 }} onChange={(e) => onChange({ ...draft, [key]: e.target.value })} />
  );
  const devicePicker = (key, label, kind, hint) => {
    const options = available(kind);
    return (
      <Box>
        <TextField select fullWidth label={label} value={draft?.[key] || ''} disabled={saving || !options.length} onChange={(e) => onChange({ ...draft, [key]: e.target.value })}>
          <MenuItem value="">Not fitted</MenuItem>
          {options.map((s) => <MenuItem key={s.id} value={s.identifier}>{s.identifier}</MenuItem>)}
        </TextField>
        <Typography variant="caption" color="text.secondary">{options.length ? hint : `No ${kind} device is free to attach. A tenant administrator registers new devices under Vehicles, on the Devices tab.`}</Typography>
      </Box>
    );
  };
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={save}>
        <DialogTitle>Add a truck</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Typography variant="body2" color="text.secondary">Register the truck at the counter. Only the first section is required; record its own devices and documents if it carries them.</Typography>
            {field('name', 'Vehicle number')}
            <TextField select fullWidth required label="Vehicle type" value={draft?.vehicleType || 'Truck'} disabled={saving} onChange={(e) => onChange({ ...draft, vehicleType: e.target.value })}>
              {['Truck', 'Trailer', 'Tanker', 'Van', 'Other'].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
            {field('owner', 'Owner / company')}
            {field('transporter', 'Transporter')}
            {field('driverName', 'Driver name')}
            {field('driverMobile', 'Driver mobile')}
            <Divider />
            <Typography variant="subtitle2">Devices fitted to this truck (optional)</Typography>
            {devicePicker('rfid', 'RFID tag', 'RFID', 'A tag fixed to the truck stays with it between trips.')}
            {devicePicker('gps', 'GPS device', 'GPS', administer ? 'Choose a unit only if one is fitted to this truck; you can then mark it as fitted.' : 'A unit fitted permanently to the truck must be recorded by a tenant administrator.')}
            {draft?.gps && (
              <TextField select fullWidth label="GPS is" value={draft?.assignmentMode || 'TEMPORARY'} disabled={saving} onChange={(e) => onChange({ ...draft, assignmentMode: e.target.value })}>
                <MenuItem value="TEMPORARY">Issued for this trip</MenuItem>
                {administer && <MenuItem value="PERMANENT">Fitted to the truck</MenuItem>}
              </TextField>
            )}
            <Divider />
            <Typography variant="subtitle2">Documents (optional)</Typography>
            {documents.map((doc, index) => (
              <Stack key={doc.kind} direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField fullWidth size="small" label={`${doc.kind} reference`} value={doc.reference} disabled={saving} inputProps={{ maxLength: 100 }} onChange={(e) => onChange({ ...draft, documents: documents.map((d, i) => (i === index ? { ...d, reference: e.target.value } : d)) })} />
                <TextField fullWidth size="small" type="date" label="Valid until" InputLabelProps={{ shrink: true }} value={doc.expiresOn} disabled={saving} onChange={(e) => onChange({ ...draft, documents: documents.map((d, i) => (i === index ? { ...d, expiresOn: e.target.value } : d)) })} />
              </Stack>
            ))}
            {incomplete && <Alert severity="warning">Give both the reference and the expiry date for each document you record.</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving}>{saving ? 'Saving…' : 'Add and select'}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AddVehicleDialog;
